import { trackPromise } from "react-promise-tracker";
import { Button } from "@shared/ui";
import { Field, InlineError, inputClassName } from "../components/Field";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import { stubAccount, stubPolicy, updateIgnition } from "../lib/axle";
import {
  getFieldHint,
  getFieldLabel,
  getMissingFieldError,
} from "../lib/fieldCopy";

// External dependencies
import _, { get, isEmpty, size, trim } from "lodash";
import { useEffect, useState } from "react";
import { AnchorSecondary } from "../components/Anchor";

// Every client uses this field for a card claim number; the value still saves as the stub policy's policyNumber.
const CREDIT_CARD_LABELS = { policyNumber: "Claim number" };

// Validation messages shown under their field rather than in the error box.
const CREDIT_CARD_NAME_ERRORS = [
  "Enter your credit card name",
  "Enter a valid credit card name, not a card number",
];

const validateCreditCardInput = (
  hasCreditCard,
  creditCardName,
  fields,
  combinedFields,
  setError
) => {
  creditCardName = trim(creditCardName);

  if (!hasCreditCard && !creditCardName) {
    setError(CREDIT_CARD_NAME_ERRORS[0]);
    return false;
  }

  // Input validation on credit card name
  // Warn user's if they've accidentally entered a credit card number
  // Check if string entered contains multiple numbers
  if (
    creditCardName &&
    creditCardName.match(/\d/g) &&
    size(creditCardName.match(/\d/g)) > 1
  ) {
    setError(CREDIT_CARD_NAME_ERRORS[1]);
    return false;
  }

  // Initialize error tracker
  let errorMessage = "";
  let hasError = false;

  // Check if any required additional fields are missing
  _(combinedFields)
    .chain()
    .filter((item) => item.required)
    .forEach((item) => {
      if (!fields[item.value] || !trim(fields[item.value])) {
        errorMessage = getMissingFieldError(item, CREDIT_CARD_LABELS);
        hasError = true;
      }
    })
    .value();

  // If one required field is missing a value, then setError
  if (hasError) {
    setError(errorMessage);
    return false;
  }

  return true;
};

// Remove any fields from the fields object that are non-required and have an empty value
// This is so that we don't send them to the backend with an empty string value
const removeEmptyNonRequiredFields = (fields, combinedFields) => {
  const nonRequiredFields = _(combinedFields).filter((item) => !item.required);

  nonRequiredFields.forEach((nonRequiredField) => {
    if (
      !fields[nonRequiredField.value] ||
      !trim(fields[nonRequiredField.value])
    ) {
      delete fields[nonRequiredField.value];
    }
  });

  return fields;
};

const ManualCreditCard = ({
  step,
  nextStep,
  setAccountInfo,
  setPolicyInfo,
  loginInformation,
  setLoginInformation,
  session,
  posthog,
}) => {
  useEffect(() => {
    const creditCardId = get(loginInformation, "id");

    const updateIgnitionManualCreditCard = async () => {
      const [updateIgnitionResponse, updateIgnitionError] =
        await updateIgnition(session.id, {
          carrier: creditCardId,
        });

      if (updateIgnitionError) {
        switch (get(updateIgnitionError, "code")) {
          case 500:
            console.log(JSON.stringify(updateIgnitionError));
            nextStep("axle-error");
            return;
          default:
            nextStep("connection-error");
            return;
        }
      }
    };
    updateIgnitionManualCreditCard();
    posthog.capture("$pageview", { step });
  }, [posthog]);

  const ignitionToken = session.id;

  const [creditCardName, setCreditCardName] = useState("");
  const [fields, setFields] = useState({});
  const [error, setError] = useState(null);

  const hasCreditCard = get(loginInformation, "id") ? true : false;
  const claimsUrl = get(loginInformation, "claimsUrl", false);
  const baseFields = [
    {
      order: 1,
      enabled: true,
      required: true,
      value: "firstName",
      type: "text",
    },
    {
      order: 2,
      enabled: true,
      required: true,
      value: "lastName",
      type: "text",
    },
  ];
  const combinedFields = _.chain()
    .unionBy(
      get(session, "config.manual-credit-card.additional-fields", []),
      baseFields,
      "value"
    )
    .sortBy(["order"], ["asc"])
    .value();

  const createStubAxleEntities = async () => {
    const carrier = hasCreditCard
      ? get(loginInformation, "id")
      : creditCardName;

    const constructedAccount = {
      firstName: get(fields, "firstName"),
      lastName: get(fields, "lastName"),
      carrier,
    };

    const processDetail = "credit-card-form";

    //TODO: remove once we validate which pathway doesn't set ignitionToken (refer to AXL-4235)
    if (isEmpty(ignitionToken)) {
      console.log(`processDetail: ${processDetail}`);
      console.error("No ignitionToken provided"); //Use this .error to search in Sentry
    }

    // Create Account
    const [account, accountError] = await stubAccount(
      ignitionToken,
      constructedAccount,
      processDetail
    );

    if (accountError) {
      return "failed";
    }

    const accountId = get(account, "id");

    // Create Policy
    const [policy, policyError] = await stubPolicy(
      ignitionToken,
      {
        account: accountId,
        carrier,
        ...fields,
      },
      processDetail
    );

    if (policyError) {
      return "failed";
    }

    // Set result to manual
    setLoginInformation({
      ...loginInformation,
      result: "manual",
      resultDetail: "credit-card-form",
    });

    // Save account information
    setAccountInfo({
      id: accountId,
      ...constructedAccount,
    });

    // Save policy information
    setPolicyInfo({ id: get(policy, "id") });

    return "success";
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (
      !validateCreditCardInput(
        hasCreditCard,
        creditCardName,
        fields,
        combinedFields,
        setError
      )
    ) {
      return;
    }

    // Filter out any additional fields that are not required and that have empty strings as their values
    const nonEmptyNonRequiredCombinedFields = removeEmptyNonRequiredFields(
      fields,
      combinedFields
    );

    setFields(nonEmptyNonRequiredCombinedFields);
    nextStep(await trackPromise(createStubAxleEntities()));
  };

  const erroredField = _.find(
    combinedFields,
    (item) =>
      item.enabled && getMissingFieldError(item, CREDIT_CARD_LABELS) === error
  );
  const fieldError = CREDIT_CARD_NAME_ERRORS.includes(error)
    ? "manual-credit-card-name"
    : erroredField && `manual-credit-card-${erroredField.value}`;

  return (
    <form className="flex min-h-0 flex-1 flex-col" onSubmit={onSubmit}>
      <StepBody>
        <StepHeading
          title="Enter your card holder information"
          description="Axle will only share the information you provide."
        />
        <div className="flex flex-col gap-4">
          {error && !fieldError && (
            <div
              className="rounded border border-danger-border p-3"
              role="status"
            >
              <InlineError>{error}</InlineError>
            </div>
          )}
          {!hasCreditCard && (
            <Field
              id="manual-credit-card-name"
              label="Credit card name"
              error={fieldError === "manual-credit-card-name" ? error : undefined}
            >
              <input
                id="manual-credit-card-name"
                aria-invalid={fieldError === "manual-credit-card-name"}
                aria-describedby={
                  fieldError === "manual-credit-card-name"
                    ? "manual-credit-card-name-error"
                    : undefined
                }
                placeholder="e.g. Chase Sapphire Reserve"
                value={creditCardName}
                className={inputClassName}
                type="text"
                onChange={(e) => setCreditCardName(e.target.value)}
              />
            </Field>
          )}

          {_(combinedFields)
            .chain()
            .filter((item) => item.enabled)
            .map((item) => (
              <Field
                key={item.value}
                id={`manual-credit-card-${item.value}`}
                label={getFieldLabel(item, CREDIT_CARD_LABELS)}
                optional={item.required === false}
                error={
                  fieldError === `manual-credit-card-${item.value}`
                    ? error
                    : undefined
                }
              >
                <input
                  id={`manual-credit-card-${item.value}`}
                  aria-invalid={fieldError === `manual-credit-card-${item.value}`}
                  aria-describedby={
                    fieldError === `manual-credit-card-${item.value}`
                      ? `manual-credit-card-${item.value}-error`
                      : undefined
                  }
                  placeholder={getFieldHint(item)}
                  value={fields[item.value]}
                  className={inputClassName}
                  type={item.type}
                  onChange={(e) =>
                    setFields({
                      ...fields,
                      [item.value]: e.target.value,
                    })
                  }
                />
              </Field>
            ))
            .value()}
        </div>
      </StepBody>
      <StepFooter>
        <Button type={"submit"}>Continue</Button>
        {claimsUrl && get(session, "config.manual-credit-card.claims") && (
          <AnchorSecondary
            url={claimsUrl}
            onClick={() => {
              // Capture posthog event for filing a credit card claim
              posthog.capture("file-credit-card-claim", { creditCardName });
            }}
            text={"Need help filing a claim?"}
          />
        )}
      </StepFooter>
    </form>
  );
};

export default ManualCreditCard;
